import React, { useState } from 'react';
import { API } from '../../utils/api';
import OnboardingShell from './OnboardingShell';
import ChoiceCard from './ChoiceCard';

// Adapted from OnboardingPlacement.dc.html: dropped the mockup's "Skip to
// Agentic Systems and Production tracks" copy on the professional option --
// those tracks don't exist yet (same reasoning as OnboardingGoal). Stored
// as a label only for now; using it to actually skip curriculum content is
// a real content-sequencing decision flagged in the build plan, not
// something to silently implement here.
const LEVELS = [
  { id: 'new', icon: 'Sprout', title: 'New to this', description: "I've used ChatGPT, never really studied how prompts work." },
  { id: 'some-experience', icon: 'TreeDeciduous', title: 'Some experience', description: 'I know few-shot and chain-of-thought, want to go deeper.' },
  { id: 'professional', icon: 'Zap', title: 'I build with this professionally', description: "I want the fast-paced, skip-what-I-know version." },
] as const;

type LevelId = typeof LEVELS[number]['id'];

interface OnboardingPlacementProps {
  onComplete: () => void;
}

const OnboardingPlacement: React.FC<OnboardingPlacementProps> = ({ onComplete }) => {
  // No default: this answer changes which rung of every lesson a learner
  // starts on (backend/src/content/lessonContent.js's startingRungFor), and
  // an unmade choice must fall through to the safe "study" default that
  // function already implements -- not silently submit whichever option
  // happened to be pre-highlighted. A learner who reads the question and
  // clicks "Finish" without consciously picking one is not the same as a
  // learner who told us they have some experience.
  const [selected, setSelected] = useState<LevelId | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async (placementLevel?: LevelId) => {
    setSaving(true);
    try {
      await API.put('/onboarding', { step: 'placement', ...(placementLevel ? { placementLevel } : {}) });
    } catch {
      // Same non-fatal handling as the goal step -- don't block on this.
    } finally {
      setSaving(false);
      onComplete();
    }
  };

  return (
    <OnboardingShell
      currentStep={2}
      totalSteps={2}
      heading="How much prompt engineering have you done?"
      subheading="We'll skip what you already know and start you where it actually gets useful."
      onContinue={() => save(selected ?? undefined)}
      onSkip={() => save()}
      continueLabel="Finish"
      continueDisabled={saving || !selected}
    >
      {LEVELS.map((level) => (
        <ChoiceCard
          key={level.id}
          icon={level.icon}
          title={level.title}
          description={level.description}
          selected={selected === level.id}
          onClick={() => setSelected(level.id)}
        />
      ))}
    </OnboardingShell>
  );
};

export default OnboardingPlacement;
